/**
 * Drill-down modal for a single Owners Fund mismatch day — causes and ledger gaps.
 */
import { useEffect, useState } from 'react';
import { fetchPumpOwnerFundDifferenceDetail } from './adminApi';
import { formatInrAmount as formatCurrency } from '../../../utils/reportFormat';

export default function OwnerFundMismatchDetailModal({
  pumpId,
  acDate,
  fromDate,
  toDate,
  onClose,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!pumpId || !acDate) return undefined;

    setLoading(true);
    setError('');
    setDetail(null);

    fetchPumpOwnerFundDifferenceDetail(pumpId, acDate, fromDate, toDate)
      .then((res) => {
        if (!res.status) {
          setError(res.message || 'Unable to load mismatch detail.');
          return;
        }
        setDetail(res.data);
      })
      .catch((err) => setError(err.message || 'Unable to load mismatch detail.'))
      .finally(() => setLoading(false));

    return undefined;
  }, [pumpId, acDate, fromDate, toDate]);

  if (!acDate) return null;

  return (
    <>
      <div className="owner-fund-modal-backdrop" onClick={onClose} aria-hidden />
      <div className="owner-fund-modal" role="dialog" aria-modal="true" aria-labelledby="owner-fund-mismatch-title">
        <div className="owner-fund-modal__head">
          <div>
            <h5 id="owner-fund-mismatch-title" className="mb-0">
              Owners Fund mismatch — {detail?.ac_date_display || acDate}
            </h5>
            <p className="small text-muted mb-0">Where it went wrong and what to fix</p>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>

        <div className="owner-fund-modal__body">
          {loading && <p className="text-muted mb-0">Analysing ledger for this date…</p>}

          {error && !loading && (
            <div className="alert alert-danger py-2 mb-0">{error}</div>
          )}

          {detail && !loading && (
            <>
              <p className="small text-muted">{detail.summary}</p>

              <table className="table table-sm table-bordered owner-fund-modal__summary-table">
                <tbody>
                  <tr>
                    <th>Prior system fund</th>
                    <td>{formatCurrency(detail.prior_system_fund)}</td>
                  </tr>
                  <tr>
                    <th>Daily P&amp;L</th>
                    <td>{formatCurrency(detail.daily_pl)}</td>
                  </tr>
                  <tr>
                    <th>Expected owners fund</th>
                    <td>{formatCurrency(detail.owners_fund)}</td>
                  </tr>
                  <tr>
                    <th>System owners fund (BS)</th>
                    <td>{formatCurrency(detail.system_owners_fund)}</td>
                  </tr>
                  <tr>
                    <th>Daily BS movement</th>
                    <td>{formatCurrency(detail.daily_bs_movement)}</td>
                  </tr>
                  <tr className="table-warning">
                    <th>Difference</th>
                    <td className="text-danger fw-bold">{formatCurrency(detail.difference)}</td>
                  </tr>
                </tbody>
              </table>

              {(detail.cause_buckets || []).length > 0 && (
                <div className="owner-fund-modal__section">
                  <h6>What went wrong — likely causes</h6>
                  <ul className="owner-fund-modal__causes">
                    {detail.cause_buckets.map((cause) => (
                      <li key={cause.code + cause.label} className="owner-fund-modal__cause">
                        <div className="owner-fund-modal__cause-head">
                          <span className="badge bg-warning text-dark">{cause.label}</span>
                          <span className="fw-bold">{formatCurrency(cause.amount)}</span>
                        </div>
                        {cause.detail && <p className="small text-muted mb-1">{cause.detail}</p>}
                        {(cause.sample_lines || []).length > 0 && (
                          <ul className="small mb-0 ps-3">
                            {cause.sample_lines.map((line, idx) => (
                              <li key={`${cause.code}-sample-${idx}`}>{line}</li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(detail.mismatched_transactions || []).length > 0 ? (
                <div className="owner-fund-modal__section">
                  <h6>Transactions where P&amp;L ≠ balance sheet</h6>
                  <p className="small text-muted">
                    These ledger groups posted different amounts to profit vs balance sheet on this date.
                  </p>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th>Trans ID</th>
                          <th>Type</th>
                          <th>P&amp;L net</th>
                          <th>BS net</th>
                          <th>Gap</th>
                          <th>Ledger lines</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.mismatched_transactions.map((tx) => (
                          <tr key={tx.trans_id}>
                            <td><code>{tx.trans_id}</code></td>
                            <td>{tx.ac_type || '—'}</td>
                            <td>{formatCurrency(tx.pl_net)}</td>
                            <td>{formatCurrency(tx.bs_net)}</td>
                            <td className="text-danger fw-bold">{formatCurrency(tx.gap)}</td>
                            <td>
                              <ul className="small mb-0 ps-3">
                                {(tx.lines || []).map((line, idx) => (
                                  <li key={`${tx.trans_id}-line-${idx}`}>{line}</li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="small text-muted mb-0">
                    Open <strong>Account Statement</strong> for {detail.ac_date_display || acDate} and search by trans_id to correct entries.
                  </p>
                </div>
              ) : (
                <p className="small text-muted mb-0">
                  No single transaction gap found — check inactive account heads or many small rounding entries across the day.
                </p>
              )}
            </>
          )}
        </div>

        <div className="owner-fund-modal__foot">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}
